/**
 * Redact sensitive fields from payloads sent to PostHog and /telemetry/client-error.
 */

const DEFAULT_MAX_DEPTH = 10;
const DEFAULT_MAX_STRING = 500;

/** Keys matched case-insensitively (substring match on normalized key). */
const SENSITIVE_KEY_FRAGMENTS = [
  'password',
  'passwd',
  'secret',
  'token',
  'authorization',
  'cookie',
  'bearer',
  'email',
  'ssn',
  'apikey',
  'api_key',
  'access_token',
  'refresh_token',
  'id_token',
  'credit_card',
  'creditcard',
  'pan',
  'routing_number',
  'account_number',
  'bankaccount',
] as const;

function keyLooksSensitive(key: string): boolean {
  const k = key.toLowerCase().replaceAll(/[-_\s]/g, '');
  for (const frag of SENSITIVE_KEY_FRAGMENTS) {
    const f = frag.replaceAll(/[-_\s]/g, '');
    if (k.includes(f)) return true;
  }
  return false;
}

export type SanitizeForClientTelemetryOptions = {
  maxDepth?: number;
  maxStringLength?: number;
};

/**
 * Deep-clone-ish sanitization: masks values whose keys look sensitive; truncates long strings.
 */
export function sanitizeForClientTelemetry(
  value: unknown,
  options?: SanitizeForClientTelemetryOptions,
  depth = 0
): unknown {
  const maxDepth = options?.maxDepth ?? DEFAULT_MAX_DEPTH;
  const maxStringLength = options?.maxStringLength ?? DEFAULT_MAX_STRING;
  if (depth > maxDepth) return '[MaxDepth]';
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') {
    return value.length > maxStringLength ? `${value.slice(0, maxStringLength)}…` : value;
  }
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'bigint') return String(value);
  if (value instanceof Error) {
    return { name: value.name, message: value.message };
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeForClientTelemetry(item, options, depth + 1));
  }
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      if (keyLooksSensitive(key)) {
        out[key] = '[REDACTED]';
      } else {
        out[key] = sanitizeForClientTelemetry(v, options, depth + 1);
      }
    }
    return out;
  }
  return String(value);
}

/** Whether full stack traces may be attached to outbound client telemetry. */
export function allowRawStacksInClientTelemetry(): boolean {
  return import.meta.env.DEV === true || import.meta.env.MODE === 'test';
}

/** One-line stack summary for production (no frame dump). */
export function summarizeStack(stack: string | undefined, maxLen = 240): string | undefined {
  if (!stack?.trim()) return undefined;
  const first = stack.split('\n')[0]?.trim() ?? stack.trim();
  return first.length > maxLen ? `${first.slice(0, maxLen)}…` : first;
}
